import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Save,
  User,
} from 'lucide-react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  accountInfoSchema,
  formatPhoneNumberForDisplay,
  locationPreferencesSchema,
  passwordChangeSchema,
} from '@/lib/validations/user-preferences'
import type {
  AccountInfoFormData,
  LocationPreferencesFormData,
  PasswordChangeFormData,
} from '@/lib/validations/user-preferences'
import {
  updateLocationPreferences,
  updatePhoneNumber,
} from '@/data/user-preferences'
import { fetchUserPreferences, useUser } from '@/hooks/useUser'
import { supabase } from '@/auth/supabase'
import type { LocationPreferences } from '@/types/core'

export const Route = createFileRoute('/_authenticated/account/settings')({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ['user-preferences', context.session?.user.id],
      queryFn: () => fetchUserPreferences(context.session!.user.id),
      staleTime: 5 * 60 * 1000,
    }),
  component: RouteComponent,
})

function RouteComponent() {
  const { user, preferences } = useUser()
  const router = useRouter()
  const queryClient = useQueryClient()

  const [isSavingPhone, setIsSavingPhone] = useState(false)
  const [isSavingLocation, setIsSavingLocation] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Account Info Form - using values instead of defaultValues
  const accountForm = useForm<AccountInfoFormData>({
    resolver: zodResolver(accountInfoSchema),
    values: {
      phoneNumber: preferences?.phone_number
        ? formatPhoneNumberForDisplay(preferences.phone_number)
        : '',
    },
  })

  // Location Form - using values instead of defaultValues
  const locationForm = useForm<LocationPreferencesFormData>({
    resolver: zodResolver(locationPreferencesSchema),
    values: {
      zipcode:
        (preferences?.location_preferences as LocationPreferences)?.zipcode ||
        '',
    },
  })

  // Password Form - this one keeps defaultValues since it doesn't depend on server data
  const passwordForm = useForm<PasswordChangeFormData>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  // Save phone number handler
  const handleSavePhone = async (data: AccountInfoFormData) => {
    setIsSavingPhone(true)

    try {
      if (data.phoneNumber) {
        await updatePhoneNumber(data.phoneNumber)
        await queryClient.invalidateQueries({ queryKey: ['user-preferences'] })
        toast.success('Phone number saved successfully!')
      }
    } catch (error) {
      console.error('Error saving phone number:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to save phone number',
      )
    } finally {
      setIsSavingPhone(false)
    }
  }

  // Save location preferences handler
  const handleSaveLocation = async (data: LocationPreferencesFormData) => {
    setIsSavingLocation(true)

    try {
      if (data.zipcode) {
        await updateLocationPreferences(data.zipcode)
        await queryClient.invalidateQueries({ queryKey: ['user-preferences'] })
        toast.success('Location preferences saved successfully!')
      }
    } catch (error) {
      console.error('Error saving location preferences:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to save location preferences',
      )
    } finally {
      setIsSavingLocation(false)
    }
  }

  // Password change handler
  const handlePasswordChange = async (data: PasswordChangeFormData) => {
    setIsChangingPassword(true)

    try {
      // Update password directly via Supabase client
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      })
      if (error) throw new Error('Failed to update password')

      // Clear password fields
      passwordForm.reset({
        newPassword: '',
        confirmPassword: '',
      })

      toast.success('Password updated successfully!')
    } catch (error) {
      console.error('Error changing password:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to update password',
      )
    } finally {
      setIsChangingPassword(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl mx-auto p-4 pt-8">
        <Button
          variant="ghost"
          onClick={() => router.history.back()}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="space-y-6">
          {/* Account Information - Only show for logged in users */}
          {user && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Account Information
                </CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <Form {...accountForm}>
                  <form
                    onSubmit={accountForm.handleSubmit(handleSavePhone)}
                    className="space-y-4"
                  >
                    <FormField
                      control={accountForm.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            Phone Number
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="(555) 555-5555"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            US phone number format (e.g., (555) 555-5555)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isSavingPhone}
                      className="w-full"
                      size="lg"
                    >
                      {isSavingPhone ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Account Details
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}

          {/* Location Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location Preferences
              </CardTitle>
              <CardDescription>
                Set your zipcode to see nearby stations and resorts first
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...locationForm}>
                <form
                  onSubmit={locationForm.handleSubmit(handleSaveLocation)}
                  className="space-y-4"
                >
                  <FormField
                    control={locationForm.control}
                    name="zipcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zipcode</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="Enter your 5-digit zipcode"
                            maxLength={5}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          US 5-digit zipcode format (e.g., 80302)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isSavingLocation}
                    className="w-full"
                    size="lg"
                  >
                    {isSavingLocation ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Location Preferences
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Password Change */}
          {user && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...passwordForm}>
                  <form
                    onSubmit={passwordForm.handleSubmit(handlePasswordChange)}
                    className="space-y-4"
                  >
                    <FormField
                      control={passwordForm.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Enter new password"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Must be at least 6 characters
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={passwordForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Confirm new password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isChangingPassword}
                      className="w-full"
                    >
                      {isChangingPassword ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Lock className="w-4 h-4 mr-2" />
                      )}
                      Change Password
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
